import { datadogRum } from '@datadog/browser-rum';
import type { ModalProps } from '@usealloy/component-library';
import {
  Box,
  Modal,
  Text,
} from '@usealloy/component-library';
import React, { useEffect } from 'react';
import type { Document, Entity } from '~/entities/types';

import { DocumentReviewer } from './DocumentReviewer';

// ============================================================
//     TYPES
// ============================================================
interface Props {
  documents: Document[];
  // This prop is not currently used but will be needed for multi-entity investigations and document upload
  // eslint-disable-next-line
  entities?: {
    [entity_token: string]: Pick<Entity, 'entity_name' | 'entity_token'>;
  };
  hideReviewActions?: boolean;
  onClose: ModalProps['onClose'];
  onDocumentsUpdate: (args: { document_token: string; entity_token: string }) => void;
  title?: string;
}

// ============================================================
//     COMPONENT
// ============================================================
export const DocumentReviewModalV2: React.FC<Props> = ({
  documents,
  hideReviewActions = false,
  onClose,
  onDocumentsUpdate,
  title = 'Entity Documents',
}) => {
  useEffect(() => {
    datadogRum.addAction('DocReviewV2Modal', { action: 'OPEN_MODAL' });
    return () => {
      datadogRum.addAction('DocReviewV2Modal', { action: 'CLOSE_MODAL' });
    };
  }, []);

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="xl"
      header={(
        <Box justifyContent="flex-start" gap={4}>
          <Text size={4} weight={500}>
            {title}
          </Text>
        </Box>
      )}
      contentStyle={{
        maxHeight: 'calc(100vh - 200px)',
        borderBottom: 'none',
        borderTop: 'none',
        paddingTop: 0,
      }}
    >
      <Box direction="column" alignItems="flex-start" justifyContent="flex-start" gap={2} style={{ flexGrow: 1 }}>
        <DocumentReviewer
          documents={documents}
          hideReviewActions={hideReviewActions}
          onRenameSuccess={({ entity_token, document_token }) => {
            onDocumentsUpdate({ document_token, entity_token });
          }}
          onReviewSuccess={({ entity_token, document_token }) => {
            onDocumentsUpdate({ entity_token, document_token });
          }}
          shouldPinReviewActions
        />
      </Box>
    </Modal>
  );
};
